import { useState, useEffect, useMemo } from "react";
import api, { formatBRL, formatDateTime, whatsappHref, apiErrorMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Phone, MessageCircle, Trophy, ArrowRight, Ban, User, Mail, Cake,
  Star, Send, RotateCcw, Building2, Edit, Save, X, Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CAT_LABEL = { cliente: "Cliente", consumidor: "Consumidor", nao_cliente: "Não Cliente" };
const CAT_CLASS = {
  cliente: "bg-emerald-500 text-white",
  consumidor: "bg-amber-500 text-white",
  nao_cliente: "bg-red-500 text-white",
};

function pad(n) { return String(n).padStart(2, "0"); }
function ymKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; }
function ymLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${meses[m - 1]}/${y}`;
}

/**
 * Perfil detalhado da Pessoa com timeline filtrada.
 * Filtros: "30d" (últimos 30 dias) por padrão | "YYYY-MM" (mês específico) | "todos" (histórico).
 */
export default function PessoaPerfilModal({ open, onClose, pessoaId, municipioNome, onRegistrarContato, onExcluido }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("30d"); // "30d" | "todos" | "YYYY-MM"
  // Iter 39: edição inline dos dados da pessoa
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  // Iter 42: soft delete
  const [excluindo, setExcluindo] = useState(false);

  useEffect(() => {
    if (!open || !pessoaId) { setData(null); setEditando(false); return; }
    setFiltro("30d");
    setEditando(false);
    api.get(`/pessoas/${pessoaId}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData({ pessoa: null, eventos: [], totais: {} }));
  }, [open, pessoaId]);

  const iniciarEdicao = () => {
    if (!data?.pessoa) return;
    const p = data.pessoa;
    setForm({
      nome: p.nome || "",
      nome_urna: p.nome_urna || "",
      funcao: p.funcao || "",
      partido: p.partido || "",
      telefone: p.telefone || "",
      whatsapp: p.whatsapp || "",
      email: p.email || "",
      instagram: p.instagram || "",
      data_nascimento: p.data_nascimento || "",
    });
    setEditando(true);
  };

  const salvarEdicao = async () => {
    if (!form.nome?.trim()) { toast.error("Nome é obrigatório"); return; }
    setSalvando(true);
    try {
      // PATCH espera todos os campos do PessoaIn (nome, funcao, municipio_id, etc)
      // Passamos os essenciais editáveis + preservamos o municipio_id existente
      const p = data.pessoa;
      const payload = {
        municipio_id: p.municipio_id,
        nome: form.nome.trim(),
        nome_urna: form.nome_urna || null,
        funcao: form.funcao || null,
        partido: form.partido || null,
        telefone: form.telefone || null,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
        instagram: form.instagram || null,
        data_nascimento: form.data_nascimento || null,
      };
      await api.patch(`/pessoas/${pessoaId}`, payload);
      toast.success("Dados atualizados");
      // recarrega
      const r = await api.get(`/pessoas/${pessoaId}/timeline`);
      setData(r.data);
      setEditando(false);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao salvar"));
    } finally {
      setSalvando(false);
    }
  };

  // Iter 42: exclusão (soft delete). Só aparece para diretoria.
  const excluirPessoa = async () => {
    if (!data?.pessoa) return;
    if (!window.confirm(
      `Excluir ${data.pessoa.nome}?\n\n` +
      `A pessoa será removida das listas, mas o histórico (contatos e inscrições) permanecerá para auditoria.\n\n` +
      `Esta ação é reservada à Diretoria.`
    )) return;
    setExcluindo(true);
    try {
      await api.delete(`/pessoas/${pessoaId}`);
      toast.success("Pessoa excluída");
      onExcluido?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao excluir"));
    } finally {
      setExcluindo(false);
    }
  };

  const mesesDisponiveis = useMemo(() => {
    // últimos 12 meses (mais atual primeiro)
    const now = new Date();
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(ymKey(d));
    }
    return out;
  }, []);

  const eventosFiltrados = useMemo(() => {
    if (!data?.eventos) return [];
    if (filtro === "todos") return data.eventos;
    if (filtro === "30d") {
      const corte = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      return data.eventos.filter((e) => (e.data || "") >= corte);
    }
    // YYYY-MM
    return data.eventos.filter((e) => (e.data || "").slice(0, 7) === filtro);
  }, [data, filtro]);

  const p = data?.pessoa;
  const cat = p?.categoria || (p?.frequencia >= 2 ? "cliente" : p?.frequencia === 1 ? "consumidor" : "nao_cliente");
  const waHref = whatsappHref(p?.whatsapp || p?.telefone);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="modal-pessoa-perfil">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            <span className="font-display text-2xl">{p?.nome || "Carregando..."}</span>
            {p?.nome_urna && (
              <span className="text-sm font-normal text-slate-500 italic" data-testid="pessoa-nome-urna">
                ({p.nome_urna})
              </span>
            )}
            {p?.ponto_focal && (
              <span className="text-[10px] uppercase font-bold tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded">
                <Star className="w-3 h-3 inline -mt-0.5 mr-0.5 fill-white" /> Ponto Focal
              </span>
            )}
            {p?.institucional && (
              <span className="text-[10px] uppercase font-bold tracking-widest bg-slate-300 text-slate-800 px-1.5 py-0.5 rounded">
                Institucional
              </span>
            )}
            {p && !p.institucional && (
              <span className={`text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded ${CAT_CLASS[cat] || "bg-slate-300 text-slate-800"}`}>
                {CAT_LABEL[cat]}
              </span>
            )}
            {p?.blacklist && (
              <span className="text-[10px] uppercase font-bold tracking-widest bg-red-600 text-white px-1.5 py-0.5 rounded flex items-center gap-1">
                <Ban className="w-3 h-3" /> Blacklist
              </span>
            )}
            {p && !editando && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={iniciarEdicao}
                data-testid="btn-editar-pessoa"
              >
                <Edit className="w-3.5 h-3.5 mr-1" /> Editar dados
              </Button>
            )}
            {/* Iter 42: exclusão de pessoa — somente diretoria */}
            {p && !editando && user?.role === "diretoria" && (
              <Button
                variant="outline"
                size="sm"
                onClick={excluirPessoa}
                disabled={excluindo}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid="btn-excluir-pessoa"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> {excluindo ? "Excluindo…" : "Excluir"}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Perfil da pessoa com timeline filtrável dos últimos 30 dias ou por mês.
          </DialogDescription>
          {p && !editando && (
            <div className="text-xs text-slate-500 space-y-0.5 mt-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span>{p.funcao}</span>
                {p.partido && <span className="text-slate-400">({p.partido})</span>}
                {municipioNome && (
                  <span className="flex items-center gap-1">
                    · <Building2 className="w-3 h-3" /> {municipioNome}
                  </span>
                )}
                {typeof p.frequencia === "number" && p.frequencia > 0 && (
                  <span className="bg-amber-50 text-amber-700 font-semibold px-1.5 rounded">
                    {p.frequencia}× em cursos
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {p.telefone && (
                  <a
                    href={whatsappHref(p.telefone)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-700 hover:underline flex items-center gap-1 font-medium"
                    data-testid="pessoa-perfil-telefone"
                  >
                    <Phone className="w-3 h-3" /> {p.telefone}
                  </a>
                )}
                {p.whatsapp && p.whatsapp !== p.telefone && (
                  <a
                    href={whatsappHref(p.whatsapp)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-700 hover:underline flex items-center gap-1 font-medium"
                    data-testid="pessoa-perfil-whatsapp"
                  >
                    <MessageCircle className="w-3 h-3" /> WA: {p.whatsapp}
                  </a>
                )}
                {p.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3 text-slate-400" /> {p.email}
                  </span>
                )}
                {p.data_nascimento && (
                  <span className="flex items-center gap-1">
                    <Cake className="w-3 h-3 text-pink-500" /> {(() => {
                      // "2000-05-15" -> "15/05"
                      const parts = String(p.data_nascimento).split("-");
                      return parts.length >= 3 ? `${parts[2].slice(0, 2)}/${parts[1]}` : p.data_nascimento;
                    })()}
                  </span>
                )}
              </div>
            </div>
          )}
          {p && editando && (
            <div className="mt-3 bg-blue-50/50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="pessoa-edit-form">
              <div className="text-[10px] uppercase font-bold tracking-widest text-blue-700 mb-1">Editando dados</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Nome *</Label>
                  <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="pessoa-edit-nome" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Nome de urna / Apelido</Label>
                  <Input value={form.nome_urna} onChange={(e) => setForm({ ...form, nome_urna: e.target.value })} placeholder="Ex: João do Bairro" data-testid="pessoa-edit-nome-urna" />
                </div>
                <div>
                  <Label className="text-xs">Função / Cargo</Label>
                  <Input value={form.funcao} onChange={(e) => setForm({ ...form, funcao: e.target.value })} placeholder="Ex: Vereador, Presidente" data-testid="pessoa-edit-funcao" />
                </div>
                <div>
                  <Label className="text-xs">Partido</Label>
                  <Input value={form.partido} onChange={(e) => setForm({ ...form, partido: e.target.value })} data-testid="pessoa-edit-partido" />
                </div>
                <div>
                  <Label className="text-xs">Telefone</Label>
                  <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="pessoa-edit-tel" />
                </div>
                <div>
                  <Label className="text-xs">WhatsApp</Label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="pessoa-edit-wa" />
                </div>
                <div>
                  <Label className="text-xs">E-mail</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pessoa-edit-email" />
                </div>
                <div>
                  <Label className="text-xs">Instagram</Label>
                  <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="pessoa-edit-ig" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Data de nascimento</Label>
                  <Input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} data-testid="pessoa-edit-nasc" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditando(false)} disabled={salvando} data-testid="btn-cancelar-edit-pessoa">
                  <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                </Button>
                <Button size="sm" onClick={salvarEdicao} disabled={salvando} data-testid="btn-salvar-edit-pessoa">
                  <Save className="w-3.5 h-3.5 mr-1" /> {salvando ? "Salvando…" : "Salvar"}
                </Button>
              </div>
            </div>
          )}
          {p?.blacklist && p?.motivo_blacklist && (
            <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1">
              <b>Motivo blacklist:</b> <i>{p.motivo_blacklist}</i>
            </div>
          )}
        </DialogHeader>

        {/* KPIs rápidos */}
        {data && (
          <div className="grid grid-cols-3 gap-3 pt-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Contatos</div>
              <div className="font-display text-2xl font-bold tabular-nums text-blue-700">
                {data.totais?.contatos ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Inscrições</div>
              <div className="font-display text-2xl font-bold tabular-nums text-emerald-700">
                {data.totais?.inscricoes ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Eventos totais</div>
              <div className="font-display text-2xl font-bold tabular-nums text-slate-700">
                {data.totais?.eventos ?? 0}
              </div>
            </div>
          </div>
        )}

        {/* Filtro */}
        <div className="flex items-center gap-2 pt-3 flex-wrap">
          <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Período:</span>
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="w-56" data-testid="pessoa-perfil-filtro-periodo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="todos">Todo o histórico</SelectItem>
              {mesesDisponiveis.map((ym) => (
                <SelectItem key={ym} value={ym}>{ymLabel(ym)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtro !== "30d" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFiltro("30d")}
              className="text-xs"
              data-testid="pessoa-perfil-limpar-filtro"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Voltar para 30d
            </Button>
          )}
          <div className="ml-auto text-xs text-slate-500 tabular-nums" data-testid="pessoa-perfil-contador">
            {eventosFiltrados.length} evento{eventosFiltrados.length !== 1 ? "s" : ""} no período
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-auto mt-2 pr-1">
          {!data && <div className="text-sm text-slate-400 py-6">Carregando timeline...</div>}
          {data && eventosFiltrados.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center italic border border-dashed border-slate-200 rounded-lg">
              Sem eventos nesse período.
            </div>
          )}
          <div className="space-y-2">
            {eventosFiltrados.map((e, i) => (
              <div
                key={`${e.tipo}-${e.data}-${e.payload?.id || e.payload?.pessoa_id || i}`}
                className="flex items-start gap-2 text-sm border-l-2 border-slate-200 pl-3 py-1.5"
                data-testid={`pessoa-tl-item-${i}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {e.tipo === "contato" && (e.payload.canal === "ligacao"
                    ? <Phone className="w-4 h-4 text-blue-500" />
                    : <MessageCircle className="w-4 h-4 text-emerald-500" />)}
                  {e.tipo === "inscricao" && <Trophy className="w-4 h-4 text-amber-500" />}
                  {e.tipo === "blacklist_pessoa" && <Ban className="w-4 h-4 text-red-500" />}
                  {e.tipo === "prospeccao_enviada" && <Send className="w-4 h-4 text-purple-500" />}
                  {e.tipo === "categoria_mudou" && <ArrowRight className="w-4 h-4 text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  {e.tipo === "contato" && (
                    <div>
                      <span className="text-slate-500">
                        {e.payload.canal === "ligacao" ? "📞 Ligação " : "💬 WhatsApp "}
                      </span>
                      {e.payload.observacao && (
                        <div className="text-slate-700 italic mt-0.5">"{e.payload.observacao}"</div>
                      )}
                    </div>
                  )}
                  {e.tipo === "inscricao" && (
                    <div>
                      <span className="font-semibold text-emerald-700">
                        🎯 Inscrição em <b>{e.payload.curso_nome || "curso"}</b>
                        {e.payload.curso_mes_ano ? ` · ${e.payload.curso_mes_ano}` : ""}
                      </span>
                      <div className="text-xs text-slate-500">
                        {formatBRL(e.payload.valor_vendido || 0)}
                        {e.payload.vendedora_nome && ` · por ${e.payload.vendedora_nome}`}
                      </div>
                    </div>
                  )}
                  {e.tipo === "blacklist_pessoa" && (
                    <div>
                      <span className="font-semibold text-red-700">
                        {e.payload.blacklist ? "Marcado como Blacklist" : "Removido da Blacklist"}
                      </span>
                      {e.payload.motivo && <div className="text-xs text-slate-500 italic">"{e.payload.motivo}"</div>}
                    </div>
                  )}
                  {e.tipo === "prospeccao_enviada" && (
                    <div className="text-slate-700">
                      Enviada para funil {e.payload.curso_nome ? <b>· {e.payload.curso_nome}</b> : ""} · estágio <b>{e.payload.estagio}</b>
                    </div>
                  )}
                  {e.tipo === "categoria_mudou" && (
                    <div className="text-slate-700">
                      Categoria alterada: <b>{CAT_LABEL[e.payload.de] || e.payload.de}</b> → <b>{CAT_LABEL[e.payload.para] || e.payload.para}</b>
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-0.5">{formatDateTime(e.data)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t flex items-center gap-2 flex-wrap">
          {waHref && (
            <Button
              asChild
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-pessoa-perfil-abrir-whatsapp"
            >
              <a href={waHref} target="_blank" rel="noreferrer">
                <MessageCircle className="w-4 h-4 mr-1" /> Abrir WhatsApp Web
              </a>
            </Button>
          )}
          {onRegistrarContato && p && (
            <>
              <Button
                variant="outline"
                onClick={() => onRegistrarContato(p, "ligacao")}
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="btn-pessoa-perfil-registrar-ligacao"
              >
                <Phone className="w-4 h-4 mr-1" /> Registrar Ligação
              </Button>
              <Button
                variant="outline"
                onClick={() => onRegistrarContato(p, "whatsapp")}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-pessoa-perfil-registrar-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-1" /> Registrar Whats
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose} className="ml-auto">Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
